import {
  langScheme as langSchemeRepository,
  updateLangScheme,
} from "../repositories/InterfaceRepository";
import { langSchemeMapper } from "../mappers/InterfaceMapper";
import { createOptions } from "../mappers/OptionsMapper";

// Deduplica solicitudes simultaneas del mismo esquema de idioma.
// Esto protege tambien contra el re-ejecutado de efectos en React.StrictMode.
const langSchemeRequests = new Map();

const normalizeLanguage = (lang) =>
  String(lang || "en").toLowerCase().startsWith("es") ? "es" : "en";

export const langSchemeService = async (lang) => {
  const normalizedLang = normalizeLanguage(lang);

  if (langSchemeRequests.has(normalizedLang)) {
    return langSchemeRequests.get(normalizedLang);
  }

  const request = (async () => {
    const langScheme = await langSchemeRepository(normalizedLang);
    langSchemeMapper(langScheme);
    return langScheme;
  })();

  langSchemeRequests.set(normalizedLang, request);

  try {
    return await request;
  } finally {
    // Solo deduplicamos mientras la solicitud esta en curso. Una llamada futura
    // explicita puede volver a consultar el backend si realmente se necesita.
    if (langSchemeRequests.get(normalizedLang) === request) {
      langSchemeRequests.delete(normalizedLang);
    }
  }
};

// Opciones de nacionalidad de un idioma concreto, SIN aplicar `langSchemeMapper`.
//
// Hace falta para traducir nacionalidades guardadas en el otro idioma: el perfil
// almacena la etiqueta ("Benin", "Mexicano") y no el codigo ISO, asi que para pasar
// de una a otra hay que conocer las etiquetas de los dos idiomas.
//
// Deliberadamente NO usa `langSchemeService`: ese corre el mapper, que sobreescribe
// los catalogos y los textos en localStorage. Pedir el esquema del idioma que NO
// esta en pantalla dejaria la interfaz con las etiquetas del idioma equivocado.
//
// Se cachea en memoria porque el catalogo de paises no cambia dentro de una sesion.
const nationalityOptionsCache = new Map();

export const getNationalityOptionsService = async (lang) => {
  const normalizedLang = normalizeLanguage(lang);

  if (nationalityOptionsCache.has(normalizedLang)) {
    return nationalityOptionsCache.get(normalizedLang);
  }

  const request = (async () => {
    try {
      const scheme = await langSchemeRepository(normalizedLang);
      return createOptions(scheme?.nationalities || []);
    } catch (error) {
      console.error(
        "Error al obtener las nacionalidades del idioma",
        normalizedLang,
        error
      );
      // Se cachea el vacio para no reintentar en cada render: quien llama trata la
      // ausencia como "no se pudo traducir" y muestra el valor tal como llega.
      return [];
    }
  })();

  nationalityOptionsCache.set(normalizedLang, request);
  return request;
};

export const updateLangSchemeService = async (payload) => {
  try {
    const response = await updateLangScheme(payload);
    return response;
  } catch (error) {
    throw error;
  }
};
